import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button, Chip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckIcon from '@mui/icons-material/Check';
import ElliottWorking from '../../../../ui/ElliottWorking';
import { Bone, fadeInSx } from '../../../../ui/DashboardSkeletons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AIStepLayout } from './AIStepLayout';
import { useProgramCreation } from '../../ProgramCreationContext';
import AddFileModal from './AddFileModal';
import AddTextModal from './AddTextModal';
import { API, OrgDocument } from '../../../../../api';
import { structureResponseToManualModules } from './mappers';
import { useProgramContext } from '../../../../../hooks/useProgramContext';

interface DocumentItem {
  id: string;
  name: string;
  type: 'file' | 'text' | 'url';
  status: 'processing' | 'ready';
}

type GenerationPhase = 'idle' | 'structure' | 'content' | 'completed';

export default function StepAI_Documents() {
  const navigate = useNavigate();
  const { state, dispatch } = useProgramCreation();
  const { groupId: parentGroupId = '' } = useProgramContext();

  const [orgDocs, setOrgDocs] = useState<OrgDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [openTextModal, setOpenTextModal] = useState(false);
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('idle');
  const lastUploadedId = useRef<string | null>(null);

  const effectiveGroupId = state.groupId ?? parentGroupId;

  useEffect(() => {
    if (!state.groupId && parentGroupId) {
      dispatch({ type: 'SET_GROUP_ID', payload: parentGroupId });
    }
  }, [state.groupId, parentGroupId, dispatch]);
  const selectedDocIds = state.ai.selectedDocIds || [];
  const isGenerating =
    generationPhase === 'structure' ||
    generationPhase === 'content' ||
    state.ai.structureLoading ||
    state.ai.contentLoading ||
    state.ai.contentPollingActive;

  const fetchDocuments = useCallback(
    async (withLoader = true) => {
      if (!effectiveGroupId) return [];
      if (withLoader) setLoadingDocs(true);
      setDocsError(null);

      try {
        const response = await API.getOrganizationDocuments(effectiveGroupId);
        const docs = response.documents || [];
        setOrgDocs(docs);

        if (state.ai.pendingDocs?.length) {
          state.ai.pendingDocs.forEach((pending) => {
            const readyDoc = docs.find((doc) => doc._id === pending.id && doc.documentProcessingStatus === 'indexed');
            if (readyDoc) {
              dispatch({ type: 'UPDATE_DOC_STATUS', payload: { id: pending.id, status: 'ready' } });
            }
          });
        }

        if (lastUploadedId.current) {
          const matched = docs.find(
            (doc) => doc._id === lastUploadedId.current && doc.documentProcessingStatus === 'indexed'
          );
          if (matched) {
            const updatedSelection = Array.from(new Set([...selectedDocIds, matched._id]));
            dispatch({ type: 'SET_SELECTED_DOCS', payload: updatedSelection });
            lastUploadedId.current = null;
          }
        }

        return docs;
      } catch (err) {
        console.error('Failed to load documents', err);
        setDocsError('Unable to load documents. Please try again.');
        return [];
      } finally {
        if (withLoader) setLoadingDocs(false);
      }
    },
    [dispatch, effectiveGroupId, selectedDocIds, state.ai.pendingDocs]
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (!effectiveGroupId || !state.ai.pendingDocs?.length) return undefined;
    const interval = setInterval(() => {
      fetchDocuments(false);
    }, 5000);
    return () => clearInterval(interval);
  }, [effectiveGroupId, state.ai.pendingDocs, fetchDocuments]);

  const handleSelect = (id: string) => {
    const selected = selectedDocIds.includes(id)
      ? selectedDocIds.filter((docId) => docId !== id)
      : [...selectedDocIds, id];
    dispatch({ type: 'SET_SELECTED_DOCS', payload: selected });
  };

  const handleFileUpload = async (file: File) => {
    if (!effectiveGroupId) {
      toast.error('Missing workspace. Go back and define the program purpose first.');
      throw new Error('Missing group');
    }

    try {
      const response = await API.processDocument(file, effectiveGroupId);
      const docId = response?.data?.id;
      if (!docId) {
        throw new Error('Missing document id');
      }

      lastUploadedId.current = docId;
      dispatch({
        type: 'ADD_PENDING_DOC',
        payload: { id: docId, name: file.name, type: 'file', status: 'processing' }
      });
      toast.success("File uploaded. We're indexing it now which will take a few minutes.");
      await fetchDocuments(false);
    } catch (err) {
      console.error('File upload error', err);
      toast.error('The file could not be uploaded. Try again.');
      throw err;
    }
  };

  const handleAddText = async (title: string, body: string) => {
    if (!effectiveGroupId) {
      toast.error('Missing workspace. Go back and define the program purpose first.');
      throw new Error('Missing group');
    }

    try {
      const response = await API.processTextDocument({ title, content: body, groupId: effectiveGroupId });
      const docId = response?.data?.id;
      if (!docId) {
        throw new Error('Missing text document id');
      }

      lastUploadedId.current = docId;
      dispatch({
        type: 'ADD_PENDING_DOC',
        payload: { id: docId, name: title, type: 'text', status: 'processing' }
      });
      toast.success("Text added. We're indexing it now which will take a few minutes.");
      await fetchDocuments(false);
    } catch (err) {
      console.error('Add text error', err);
      toast.error('The text could not be saved. Try again.');
      throw err;
    }
  };

  const handleGenerateProgram = async () => {
    if (!selectedDocIds.length) {
      toast.error('Select at least one document for Elliott to use.');
      return;
    }

    if (!effectiveGroupId || !state.generationId) {
      toast.error('We could not find the workspace for this program. Please start the build again.');
      return;
    }

    try {
      setGenerationPhase('structure');
      dispatch({ type: 'SET_AI_STRUCTURE_LOADING', payload: true });

      const structureResponse = await API.generateProgramStructure({
        generalPurpose: state.ai.purpose.description,
        learningObjectives: state.ai.objectives,
        groupId: effectiveGroupId,
        orgDocsIds: selectedDocIds,
        programGenerationId: state.generationId
      });

      const structurePayload = structureResponse?.data ?? structureResponse;
      dispatch({ type: 'SET_AI_STRUCTURE', payload: structurePayload });

      const manualModules = structureResponseToManualModules(structurePayload);
      if (manualModules.length) {
        dispatch({ type: 'SET_MODULES', payload: manualModules });
      }

      navigate('../review');
    } catch (err) {
      console.error('AI generation error', err);
      toast.error('We were unable to build the program. Please try again.');
    } finally {
      dispatch({ type: 'SET_AI_STRUCTURE_LOADING', payload: false });
      setGenerationPhase('idle');
    }
  };

  const nextDisabled = !selectedDocIds.length || !effectiveGroupId || !state.generationId || isGenerating;
  const progressMessage =
    generationPhase === 'content' || state.ai.contentLoading || state.ai.contentPollingActive
      ? 'Writing the Lesson Body...'
      : 'Building the program structure...';
  const phrases = [
    'Give me a minute while I build the structure of your program...',
    'Analyzing purpose. Parsing objectives. Retrieving content.',
    'Sharpening virtual pencils and building your program structure.',
    'Designing a journey to turn knowledge into capability.'
  ];
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    if (generationPhase !== 'structure') return;
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, 20000);
    return () => clearInterval(interval);
  }, [generationPhase, phrases.length]);

  const files = orgDocs.filter((doc) => doc.type === 'file');
  const texts = orgDocs.filter((doc) => doc.type === 'text');
  const renderDocRow = (doc: OrgDocument, ready: boolean) => {
    const isSelected = selectedDocIds.includes(doc._id);
    const badgeLabel = (doc.fileType?.split('/')?.pop() || doc.type || '').toUpperCase();
    return (
      <Box
        key={doc._id}
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{ opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }}
        onClick={() => ready && handleSelect(doc._id)}
      >
        <Box display="inline-flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 30,
                height: 30,
                p: '2px 4px',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '4px',
                  border: '2px solid var(--Stroke-icon-dim, #868686)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 3,
                    left: 3,
                    right: 3,
                    height: 2,
                    background: 'var(--Stroke-icon-dim, #868686)'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 3,
                    right: 3,
                    height: 2,
                    background: 'var(--Stroke-icon-dim, #868686)'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 13,
                    left: 3,
                    right: 3,
                    height: 2,
                    background: 'var(--Stroke-icon-dim, #868686)'
                  }}
                />
              </Box>
            </Box>
            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 500, lineHeight: '14px' }}>
              {doc.title}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: '5px',
                background: 'var(--gray-700, #444444)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', lineHeight: '14px' }}
              >
                {badgeLabel}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1.25}>
            {/* Not-ready docs can't be selected yet — show why instead of the
                selection control so it's clear the asset isn't usable. */}
            {!ready ? (
              doc.documentProcessingStatus === 'failed' ? (
                <Typography sx={{ color: '#F44336', fontSize: 12, fontWeight: 600 }}>Failed</Typography>
              ) : (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <CircularProgress size={12} sx={{ color: '#F79420' }} />
                  <Typography sx={{ color: '#F79420', fontSize: 12, fontWeight: 600 }}>Indexing</Typography>
                </Box>
              )
            ) : (
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isSelected ? '#00972D' : 'transparent',
                  border: isSelected ? 'none' : '2px solid #FDFDFD'
                }}
              >
                {isSelected && <CheckIcon sx={{ fontSize: 16, color: '#FDFDFD' }} />}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <AIStepLayout
      title="Document upload"
      stepTitle="Document upload"
      onBack={() => navigate(-1)}
      onNext={handleGenerateProgram}
      nextDisabled={nextDisabled}
      nextLabel="Build"
    >
      {generationPhase === 'structure' ? (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <ElliottWorking size="full" message={phrases[phraseIndex]} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.75 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography
              sx={{
                color: '#B6B6B6',
                fontSize: 14,
                fontWeight: 400,
                lineHeight: '19.88px'
              }}
            >
              What essential material should this Course include?
              <br />
              For example: your Company Handbook, Code of Conduct, Benefits Overview, or any key internal documentation.
            </Typography>
          </Box>

          {!effectiveGroupId && (
            <Typography color="error.main">
              We could not find the workspace for this build. Please go back and define the program purpose again.
            </Typography>
          )}

          <Box
            sx={{
              // maxWidth: 300,
              p: 1.25,
              background: 'var(--Fill-tooltip-dim, #2A2A2A)',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500, lineHeight: '18px' }}>
                We will use these documents to extract contextually relevant program content.
                <br />
                Uploaded documents are not shared with your people.
              </Typography>
            </Box>
            <Button
              variant="text"
              sx={{ color: '#FDFDFD', textDecoration: 'underline', fontSize: 12, fontWeight: 600 }}
              onClick={() => fetchDocuments(false)}
              disabled={loadingDocs}
            >
              Dismiss
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.875, width: '100%' }}>
            <Box
              onClick={() => setOpenFileModal(true)}
              sx={{
                flex: 1,
                p: '12px 15px',
                borderRadius: '6px',
                outline: '1px solid #868686',
                outlineOffset: '-1px',
                display: 'flex',
                gap: 1.25,
                cursor: 'pointer'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                  <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                    Add Files
                  </Typography>
                  <UploadFileIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
                </Box>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
                  Drop in relevant documents like Word Docs or PDFs that contain content relevant to your program.
                </Typography>
              </Box>
            </Box>

            <Box
              onClick={() => setOpenTextModal(true)}
              sx={{
                flex: 1,
                p: '12px 15px',
                borderRadius: '6px',
                outline: '1px solid #868686',
                outlineOffset: '-1px',
                display: 'flex',
                gap: 1.25,
                cursor: 'pointer'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                  <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                    Add Text
                  </Typography>
                  <ArticleIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
                </Box>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '20px' }}>
                  Type anything relevant to your program or copy text from other sources and paste here.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                p: '12px 15px',
                borderRadius: '6px',
                outline: '1px solid #868686',
                outlineOffset: '-1px',
                display: 'flex',
                gap: 1.25,
                cursor: 'not-allowed',
                opacity: 0.6
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                  <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '14px' }}>
                    Add URL
                  </Typography>
                  <ArrowForwardIosIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
                </Box>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '17.04px' }}>
                  Share URLs to webpages or online resources that will help us understand your program.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.125, width: '100%' }}>
            <Typography sx={{ color: '#B6B6B6', fontSize: 16, fontWeight: 400 }}>Added files:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {docsError && (
                <Typography color="error.main" sx={{ mb: 1 }}>
                  {docsError}
                </Typography>
              )}
              {loadingDocs ? (
                <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {[0, 1, 2].map((i) => (
                    <Bone key={i} height={30} />
                  ))}
                </Box>
              ) : (
                <Box sx={{ ...fadeInSx, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {state.ai.pendingDocs?.map((doc) =>
                    renderDocRow(
                      {
                        _id: doc.id,
                        title: doc.name,
                        type: doc.type as any,
                        fileType: '',
                        documentProcessingStatus: 'processing'
                      } as OrgDocument,
                      false
                    )
                  )}
                  {files.length === 0 && state.ai.pendingDocs?.length === 0 && (
                    <Typography sx={{ color: '#868686' }}>No files uploaded yet.</Typography>
                  )}
                  {files.map((doc) => {
                    const ready = doc.documentProcessingStatus === 'indexed';
                    return renderDocRow(doc, ready);
                  })}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.125, width: '100%' }}>
            <Typography sx={{ color: '#B6B6B6', fontSize: 16, fontWeight: 400 }}>Added text:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {texts.length === 0 && <Typography sx={{ color: '#868686' }}>No text added yet.</Typography>}
              {texts.map((doc) => {
                const ready = doc.documentProcessingStatus === 'indexed';
                return renderDocRow(doc, ready);
              })}
            </Box>
          </Box>

          {isGenerating && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#1F1F1F',
                borderRadius: 1,
                border: '1px solid #333'
              }}
            >
              <ElliottWorking message={progressMessage} />
            </Box>
          )}

          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600, lineHeight: '20.8px' }}>
            Click on Next when you finish uploading your desired contents.
          </Typography>
        </Box>
      )}

      <AddFileModal open={openFileModal} onClose={() => setOpenFileModal(false)} onUpload={handleFileUpload} />
      <AddTextModal open={openTextModal} onClose={() => setOpenTextModal(false)} onCreate={handleAddText} />
    </AIStepLayout>
  );
}
